<template>
  <v-container fluid class="debtors-page">
    <h1 class="text-h5 font-weight-bold mb-1">
      <v-icon left color="error">mdi-account-alert</v-icon>{{ $t('debtors.title') }}
    </h1>
    <div class="caption grey--text mb-4">{{ $t('debtors.subtitle') }}</div>

    <div class="d-flex flex-wrap align-center mb-3" style="gap: 16px;">
      <v-card outlined style="min-width: 260px;">
        <v-card-text class="d-flex align-center py-3">
          <div>
            <div class="text-overline grey--text">{{ $t('debtors.totalDebt') }}</div>
            <div class="text-h5 font-weight-bold error--text">{{ formatPln(totalDebt) }}</div>
          </div>
          <v-spacer></v-spacer>
          <v-chip color="error" outlined>{{ count }} {{ $t('debtors.debtorsCount') }}</v-chip>
        </v-card-text>
      </v-card>
      <v-text-field
        v-model="search"
        @input="onSearch"
        :label="$t('debtors.search')"
        outlined dense clearable hide-details
        prepend-inner-icon="mdi-magnify"
        style="max-width: 360px;"
      />
      <v-btn icon :loading="loading" @click="load" :title="$t('debtors.refresh')"><v-icon>mdi-refresh</v-icon></v-btn>
    </div>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="debtors"
        :loading="loading"
        item-key="driverId"
        :items-per-page="25"
        :no-data-text="$t('debtors.empty')"
      >
        <template v-slot:[`item.name`]="{ item }">
          <b>{{ item.firstName }} {{ item.lastName }}</b>
        </template>
        <template v-slot:[`item.cooperationType`]="{ item }">
          <v-chip x-small outlined>{{ item.cooperationType }}</v-chip>
        </template>
        <template v-slot:[`item.earnings`]="{ item }">{{ formatPln(item.earnings) }}</template>
        <template v-slot:[`item.rent`]="{ item }">{{ item.rent ? '−' + formatPln(item.rent) : '—' }}</template>
        <template v-slot:[`item.balance`]="{ item }">
          <span class="error--text font-weight-bold">{{ formatPln(item.balance) }}</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            x-small color="primary" outlined
            :disabled="!item.phone"
            :title="item.phone ? 'Wyślij SMS z przypomnieniem' : 'Brak numeru telefonu'"
            @click="openSmsDialog(item)"
          >
            <v-icon left x-small>mdi-message-alert-outline</v-icon>SMS
          </v-btn>
          <v-btn
            x-small color="success" outlined class="ml-2"
            title="Zaksięguj wpłatę na poczet zadłużenia"
            @click="openWplataDialog(item)"
          >
            <v-icon left x-small>mdi-cash-check</v-icon>Spłacił
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialog: wpłata na poczet zadłużenia -->
    <v-dialog v-model="wplataDialog" max-width="520" persistent>
      <v-card>
        <v-card-title>
          <v-icon left color="success">mdi-cash-check</v-icon>
          Wpłata na poczet zadłużenia
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <b>{{ wplataTarget.firstName }} {{ wplataTarget.lastName }}</b>
            <v-chip x-small color="error" outlined class="ml-2">
              saldo {{ formatPln(wplataTarget.balance) }}
            </v-chip>
          </div>

          <v-text-field
            v-model.number="wplataKwota"
            label="Kwota wpłaty (zł)"
            type="number" step="0.01" min="0"
            outlined dense autofocus
            prepend-inner-icon="mdi-currency-eur"
            :hint="podpowiedzKwoty" persistent-hint
            class="mb-3"
          />
          <v-menu v-model="wplataMenuDaty" :close-on-content-click="false" max-width="290">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatDate(wplataData)"
                label="Kiedy wpłacił" readonly outlined dense
                prepend-inner-icon="mdi-calendar" v-bind="attrs" v-on="on"
                hint="Wpłata wejdzie do rozliczenia z tą datą — ma znaczenie, do którego tygodnia trafi."
                persistent-hint class="mb-3"
              />
            </template>
            <v-date-picker v-model="wplataData" :max="dzisiaj" locale="pl" first-day-of-week="1"
              @input="wplataMenuDaty = false" />
          </v-menu>
          <v-text-field
            v-model="wplataOpis"
            label="Opis" outlined dense counter="200" maxlength="200"
            placeholder="np. przelew na konto firmowe, gotówka w biurze"
            hint="Po czym rozpoznać tę wpłatę za pół roku."
            persistent-hint
          />

          <v-alert v-if="saldoPo !== null" dense text :type="saldoPo >= 0 ? 'success' : 'info'" class="mt-4 mb-0">
            Saldo po zaksięgowaniu: <strong>{{ formatPln(saldoPo) }}</strong>
            <span v-if="saldoPo >= 0"> — dług spłacony w całości.</span>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="wplataZapisuje" @click="wplataDialog = false">Anuluj</v-btn>
          <v-btn
            color="success" :loading="wplataZapisuje"
            :disabled="!(wplataKwota > 0) || !wplataOpis.trim()"
            @click="zapiszWplate"
          >
            <v-icon left small>mdi-check</v-icon>Zaksięguj
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: SMS z przypomnieniem o zadłużeniu -->
    <v-dialog v-model="smsDialog" max-width="540">
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-message-alert-outline</v-icon>
          SMS — przypomnienie o zadłużeniu
        </v-card-title>
        <v-card-text>
          <div class="mb-3">
            Do: <b>{{ smsTarget.firstName }} {{ smsTarget.lastName }}</b>
            <span class="grey--text ml-2">{{ smsTarget.phone }}</span>
            <v-chip x-small color="error" outlined class="ml-2">saldo {{ formatPln(smsTarget.balance) }}</v-chip>
          </div>
          <v-textarea
            v-model="smsMessage"
            label="Treść wiadomości"
            outlined rows="5" auto-grow counter
          />
          <div class="caption grey--text">Treść możesz dowolnie zmienić przed wysłaniem. Wiadomość trafi do historii SMS.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="smsSending" @click="smsDialog = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="smsSending" :disabled="!smsMessage.trim()" @click="sendSms">
            <v-icon left small>mdi-send</v-icon>Wyślij SMS
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Swal from 'sweetalert2';
const axios = require('axios');

export default {
  name: 'DebtorsPage',
  data() {
    return {
      loading: false,
      debtors: [],
      totalDebt: 0,
      count: 0,
      search: '',
      expanded: [],
      searchTimer: null,
      smsDialog: false,
      smsSending: false,
      smsTarget: {},
      smsMessage: '',

      wplataDialog: false,
      wplataZapisuje: false,
      wplataTarget: {},
      wplataKwota: null,
      wplataData: new Date().toISOString().slice(0, 10),
      wplataOpis: '',
      wplataMenuDaty: false,
    };
  },
  computed: {
    dzisiaj() {
      return new Date().toISOString().slice(0, 10);
    },
    /** Ile zostaje długu po tej wpłacie — liczone na żywo, żeby nie trzeba było liczyć w głowie. */
    saldoPo() {
      if (!(this.wplataKwota > 0) || this.wplataTarget.balance === undefined) return null;
      return this.wplataTarget.balance + Math.round(this.wplataKwota * 100);
    },
    podpowiedzKwoty() {
      if (this.wplataTarget.balance === undefined) return '';
      return `Całe zadłużenie to ${this.formatPln(Math.abs(this.wplataTarget.balance))}.`;
    },
    headers() {
      return [
        { text: this.$t('debtors.driver'), value: 'name', sortable: false },
        { text: this.$t('debtors.type'), value: 'cooperationType', sortable: false },
        { text: this.$t('debtors.earnings'), value: 'earnings', align: 'end', sortable: false },
        { text: this.$t('debtors.rent'), value: 'rent', align: 'end', sortable: false },
        { text: this.$t('debtors.balance'), value: 'balance', align: 'end' },
        { text: '', value: 'actions', align: 'end', sortable: false },
      ];
    },
  },
  created() {
    this.load();
  },
  methods: {
    authHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('token') };
    },
    apiUrl(p) {
      return process.env.VUE_APP_ROOT_API + p;
    },
    formatPln(groszy) {
      const v = (groszy || 0) / 100;
      return v.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
    },
    formatDate(d) {
      try { return new Date(d).toLocaleDateString('pl-PL'); } catch (e) { return d; }
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.load, 400);
    },
    openWplataDialog(item) {
      this.wplataTarget = item;
      // Domyślnie całe zadłużenie — najczęstszy przypadek to spłata w całości,
      // a kwotę i tak widać przed zatwierdzeniem.
      this.wplataKwota = Math.abs(item.balance) / 100;
      this.wplataData = this.dzisiaj;
      this.wplataOpis = '';
      this.wplataDialog = true;
    },

    async zapiszWplate() {
      this.wplataZapisuje = true;
      try {
        const res = await axios({
          method: 'post',
          url: this.apiUrl(`settlements/debtors/${this.wplataTarget.driverId}/wplata`),
          headers: this.authHeaders(),
          data: {
            kwotaGr: Math.round(this.wplataKwota * 100),
            data: this.wplataData,
            opis: this.wplataOpis.trim(),
          },
        });
        const zapisano = (res.data && res.data.data) || {};
        this.wplataDialog = false;
        await this.load();
        Swal.fire({
          title: 'Wpłata zaksięgowana',
          text:
            `${this.formatPln(zapisano.kwotaGr)} dla ${this.wplataTarget.firstName} ` +
            `${this.wplataTarget.lastName}. Kwota pomniejsza zadłużenie w rozliczeniach.`,
          icon: 'success',
        });
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zaksięgować wpłaty', 'error');
      } finally {
        this.wplataZapisuje = false;
      }
    },

    openSmsDialog(item) {
      this.smsTarget = item;
      const kwota = this.formatPln(Math.abs(item.balance));
      this.smsMessage =
        `BTP: przypominamy o zadluzeniu w rozliczeniach na kwote ${kwota}. ` +
        `Prosimy o pilna wplate zaleglej kwoty. W razie pytan prosimy o kontakt z biurem. Ta wiadomosc nie wymaga odpowiedzi.`;
      this.smsDialog = true;
    },
    async sendSms() {
      this.smsSending = true;
      try {
        await axios({
          method: 'post',
          url: this.apiUrl('sms/send'),
          headers: this.authHeaders(),
          data: {
            driverId: this.smsTarget.driverId,
            phone: this.smsTarget.phone,
            recipientName: `${this.smsTarget.firstName} ${this.smsTarget.lastName}`,
            message: this.smsMessage.trim(),
          },
        });
        this.smsDialog = false;
        Swal.fire({ title: 'SMS wysłany', text: `Przypomnienie trafiło do ${this.smsTarget.firstName} ${this.smsTarget.lastName}.`, icon: 'success', timer: 2200, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się wysłać SMS-a', 'error');
      } finally {
        this.smsSending = false;
      }
    },
    async load() {
      this.loading = true;
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('settlements/debtors'),
          headers: this.authHeaders(),
          params: { search: this.search || undefined },
        });
        const d = (res.data && res.data.data) || {};
        this.debtors = d.debtors || [];
        this.totalDebt = d.totalDebt || 0;
        this.count = d.count || 0;
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się wczytać listy dłużników', 'error');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.weeks-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.weeks-table th, .weeks-table td {
  padding: 4px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.weeks-table th {
  text-align: left;
  color: #78909c;
  font-weight: 600;
}
</style>
